import { useCallback, useEffect, useRef, useState } from 'react';
import { consultApi } from '../utils/consultApi';
import { consultantApi } from '../utils/consultantApi';
import { useGeminiPCM } from './useGeminiPCM';
import { costFromOpenAIUsage, costFromGeminiUsage, costFromLiveUsage, addBreakdowns } from '../utils/voicePricing';
import { reportClientEvent } from '../lib/clientLog';

export const STATES = {
  IDLE: 'idle',
  REQUESTING_MIC: 'mic',
  CONNECTING: 'connecting',
  ACTIVE: 'active',
  ENDING: 'ending',
  ERROR: 'error',
};

// OpenAI Realtime GA: WebRTC SDP offer goes to this URL with the ephemeral
// token as Bearer auth. The response's Location header contains the rtc_xxx
// call_id which we extract and forward to our backend so it can open the
// sideband WS bound to the same call.
const OPENAI_REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

// GPT-Live has no trickle path to the server, so the offer must carry its ICE
// candidates: wait for gathering to finish (10 s cap, as in OpenAI's example).
function iceGathered(pc) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', onState);
      reject(new Error('ICE gathering timed out'));
    }, 10_000);
    function onState() {
      if (pc.iceGatheringState !== 'complete') return;
      clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', onState);
      resolve();
    }
    pc.addEventListener('icegatheringstatechange', onState);
  });
}

// A WebRTC link that fails mid-call: the sideband may stay up, so only the
// browser sees it. The report names the call's session id.
function watchPeer(pc, sessionIdRef, userEndRef) {
  pc.onconnectionstatechange = () => {
    const state = pc.connectionState;
    if ((state === 'failed' || state === 'disconnected') && !userEndRef.current) {
      reportClientEvent('voice.dropped', { code: state, session_id: sessionIdRef.current });
    }
  };
}

// Extract the last URL segment from a Location header value.
function parseRtcCallId(locationHeader) {
  if (!locationHeader) return null;
  const parts = locationHeader.split('/');
  return parts[parts.length - 1] || null;
}

// Two API "adapters" — the customer flow uses /api/consult/realtime/*; the
// admin classroom uses /api/consultant/teach/realtime/*. Both have the same
// shape. Customer WS auth rides in the same-origin HttpOnly cookie; teach mode
// keeps its separate single-use ws_ticket query parameter.
export const CONSULT_API_ADAPTER = {
  startCall:  (location) => consultApi.startRealtimeCall(location),
  attachCall: (sessionId, rtcCallId) => consultApi.attachRealtimeCall(sessionId, rtcCallId),
  endCall:    () => consultApi.endRealtimeCall(),
  // GPT-Live (consult only; the classroom stays on Realtime).
  createLiveCall:    (sessionId, sdp) => consultApi.createLiveCall(sessionId, sdp),
  shareLiveLocation: (location) => consultApi.shareLiveLocation(location),
};
export const TEACH_API_ADAPTER = {
  startCall:  () => consultantApi.startTeachCall(),
  attachCall: (sessionId, rtcCallId) => consultantApi.attachTeachCall(sessionId, rtcCallId),
  endCall:    () => consultantApi.endTeachCall(),
};

// Build the Gemini WS URL from the token response. The customer credential is
// never placed in the URL: same-origin WebSocket upgrades carry the HttpOnly
// cookie automatically. Admin classroom calls still use a one-time ws_ticket.
export function buildGeminiWsUrl(tokenResponse) {
  const proto  = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host   = window.location.host;
  const params = new URLSearchParams({ session_id: tokenResponse.session_id });
  if (tokenResponse.ws_ticket) params.set('ws_ticket', tokenResponse.ws_ticket);
  return `${proto}//${host}${tokenResponse.ws_path}?${params.toString()}`;
}

// useRealtimeCall(location, opts) — `location` is the customer's pinned
// lat/lng (consult mode); null for the admin classroom (teach mode).
//
// opts = {
//   mode?: 'consult' (default) | 'teach',
//   api?:  API adapter object (defaults to CONSULT_API_ADAPTER),
//   onChange?: (change) => void   // teach-mode hook for live "تابلوی کلاس"
// }
//
// The hook is transport-agnostic AND mode-agnostic from the caller's
// perspective. Returns the same { state, startCall, endCall, toggleMute,
// isMuted, ... } shape regardless of which voice provider the backend's
// startWithFallback chose.
export function useRealtimeCall(location, opts = {}) {
  const mode = opts.mode || 'consult';
  const api  = opts.api  || CONSULT_API_ADAPTER;
  const onChange = opts.onChange || null;

  const [state, setState]   = useState(STATES.IDLE);
  const [captions, setCaptions] = useState([]);
  const [elapsedSec, setElapsed] = useState(0);
  const [error, setError]   = useState(null);
  const [isMuted, setMuted] = useState(false);
  const [transport, setTransport] = useState(null);
  const [fellBack, setFellBack]   = useState(false);
  const [model, setModel]         = useState(null);
  const [usage, setUsage] = useState({ cost: 0, tokens: {} });

  // WebRTC-specific refs (openai path).
  const pcRef        = useRef(null);
  const streamRef    = useRef(null);
  const audioElRef   = useRef(null);
  const dcRef        = useRef(null);
  // Generic refs used by both transports.
  const startTsRef   = useRef(0);
  const tickRef      = useRef(null);
  const sessionIdRef = useRef(null);
  // Set when the caller hangs up (or leaves the page), so a close that follows
  // is not reported as a dropped call.
  const userEndRef   = useRef(false);
  const lastSentLocationRef = useRef(null);
  // Model id from the token response; prices usage per model.
  const modelRef     = useRef(null);
  // GPT-Live usage: cumulative voice seconds + backend tokens per response.
  const liveUsageRef = useRef(null);
  // Invalidate abandoned startup promises before a retry can own shared refs.
  const attemptGenerationRef = useRef(0);

  // Gemini transport.
  const gemini = useGeminiPCM({
    onStatus: (s) => {
      // eslint-disable-next-line no-console
      console.debug('[gemini] status', s);
    },
    onClose: (e) => {
      attemptGenerationRef.current += 1;
      if (!userEndRef.current) {
        reportClientEvent('voice.dropped', { code: e && e.code, session_id: sessionIdRef.current });
      }
      teardownMedia();
      setState(STATES.IDLE);
    },
    onError: (err) => {
      setError(err.message || 'خطا در اتصال صوتی');
      setState(STATES.ERROR);
    },
    onInterrupted: () => { /* could surface a "speaking" indicator */ },
    onUsage: (geminiUsage) => {
      setUsage((prev) => addBreakdowns(prev, costFromGeminiUsage(geminiUsage, modelRef.current)));
    },
    // Teach-mode class change frames forwarded by the server bridge — the
    // TeachVoicePanel listens to these to render the live "تابلوی کلاس"
    // list of saved/edited/archived notes.
    onClassChange: (change) => { if (onChange) onChange(change); },
  });
  const geminiRef = useRef(gemini);
  geminiRef.current = gemini;

  const teardownMedia = useCallback(() => {
    if (tickRef.current)    { clearInterval(tickRef.current); tickRef.current = null; }
    if (pcRef.current)      { try { pcRef.current.close(); } catch {} pcRef.current = null; }
    if (streamRef.current)  { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (audioElRef.current) { try { audioElRef.current.srcObject = null; } catch {} audioElRef.current = null; }
    dcRef.current = null;
    lastSentLocationRef.current = null;
    try { geminiRef.current && geminiRef.current.end && geminiRef.current.end(); } catch {}
  }, []);

  // OpenAI data-channel event router. Two responsibilities:
  //   * Usage accumulation on response.done.response.usage (both modes).
  //   * For teach mode, surface response.function_call_arguments.done so
  //     the classroom UI can show "model is calling create_note(content=...)"
  //     IN REAL TIME — server-side dispatch happens shortly after.
  const handleEvent = useCallback((event) => {
    // GPT-Live: session.usage.updated / session.closed carry the cumulative
    // voice seconds (replace, never add); backend usage arrives once per
    // response inside response.event / response.completed.
    const live = liveUsageRef.current;
    if (live) {
      if ((event.type === 'session.usage.updated' || event.type === 'session.closed')
          && event.usage && Number.isFinite(event.usage.seconds)) {
        live.seconds = event.usage.seconds;
        setUsage(costFromLiveUsage(live));
      }
      // The call ended on the server or at OpenAI (10-minute ceiling,
      // expiry, safety cut-off, lost link): release the mic like Gemini's
      // onClose does.
      if (event.type === 'session.closed') {
        attemptGenerationRef.current += 1;
        teardownMedia();
        setState(STATES.IDLE);
        return;
      }
      const inner = event.type === 'response.event' && event.event;
      const resp = inner && ['response.completed', 'response.failed', 'response.incomplete'].includes(inner.type)
        && inner.response;
      if (resp && resp.usage && resp.id && !live.responses.has(resp.id)) {
        live.responses.add(resp.id);
        live.input  += resp.usage.input_tokens || 0;
        live.cached += (resp.usage.input_tokens_details && resp.usage.input_tokens_details.cached_tokens) || 0;
        live.output += resp.usage.output_tokens || 0;
        setUsage(costFromLiveUsage(live));
      }
      return;
    }
    if (event.type === 'response.done' && event.response && event.response.usage) {
      setUsage((prev) => addBreakdowns(prev, costFromOpenAIUsage(event.response.usage, modelRef.current)));
    }
    if (mode === 'teach' && event.type === 'response.function_call_arguments.done' && onChange) {
      // The browser sees the function CALL on its DC; the server runs the
      // tool and posts function_call_output back to OpenAI. We don't get
      // the resulting note_id here (the server has it), but the
      // {action, args} pair is enough for an "in-flight" classroom row.
      let args = {};
      try { args = JSON.parse(event.arguments || '{}'); } catch {}
      onChange({
        action: event.name,                 // 'create_note' / 'update_note' / 'archive_note' / 'search_knowledge'
        args,
        in_flight: true,                    // distinguishes from server-confirmed class_change frames (gemini)
      });
    }
  }, [mode, onChange, teardownMedia]);

  // OpenAI / WebRTC path.
  const startWebRtcCall = useCallback(async (stream, token) => {
    const pc = new RTCPeerConnection();
    pcRef.current = pc;
    watchPeer(pc, sessionIdRef, userEndRef);
    pc.addTrack(stream.getAudioTracks()[0], stream);
    pc.ontrack = (ev) => {
      const audio = new Audio();
      audio.autoplay = true;
      audio.srcObject = ev.streams[0];
      audioElRef.current = audio;
    };
    const dc = pc.createDataChannel('oai-events');
    dcRef.current = dc;
    dc.onmessage = (ev) => {
      try { handleEvent(JSON.parse(ev.data)); } catch { /* ignore non-JSON */ }
    };

    let rtcCallId = null;
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const resp = await fetch(OPENAI_REALTIME_CALLS_URL, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token.ephemeral_token}`, 'Content-Type': 'application/sdp' },
      body: offer.sdp,
    });
    if (!resp.ok) throw new Error(`SDP exchange failed (${resp.status})`);
    rtcCallId = parseRtcCallId(resp.headers.get('Location'));
    const answerSdp = await resp.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    if (rtcCallId) {
      try { await api.attachCall(token.session_id, rtcCallId); }
      catch (e) {
        throw new Error('اتصال پشتیبان برقرار نشد. لطفاً با چت متنی ادامه دهید.');
      }
    }
  }, [api, handleEvent]);

  // GPT-Live path: same media setup, but the SDP offer goes to our server,
  // which creates the session with the project key and attaches the
  // sideband (tools, greeting, close). The data channel only receives.
  // Resolves null, or the backup provider's token when OpenAI rejected the
  // session and the server started the backup instead.
  const startLiveCall = useCallback(async (stream, token) => {
    const pc = new RTCPeerConnection();
    pcRef.current = pc;
    watchPeer(pc, sessionIdRef, userEndRef);
    pc.addTrack(stream.getAudioTracks()[0], stream);
    pc.ontrack = (ev) => {
      const audio = new Audio();
      audio.autoplay = true;
      audio.srcObject = ev.streams[0];
      audioElRef.current = audio;
    };
    const dc = pc.createDataChannel('oai-events');
    dcRef.current = dc;
    let resolveStarted, rejectStarted;
    const started = new Promise((resolve, reject) => { resolveStarted = resolve; rejectStarted = reject; });
    started.catch(() => {});
    const timer = setTimeout(() => rejectStarted(new Error('GPT-Live startup timed out')), 30000);
    dc.onclose = () => rejectStarted(new Error('GPT-Live closed before session.started'));
    dc.onmessage = (ev) => {
      let event;
      try { event = JSON.parse(ev.data); } catch { return; }
      if (event.type === 'session.started') resolveStarted();
      if (event.type === 'session.closed' || event.type === 'error') {
        rejectStarted(new Error('GPT-Live startup failed'));
      }
      handleEvent(event);
    };
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await iceGathered(pc);
      const answer = await api.createLiveCall(token.session_id, pc.localDescription.sdp);
      if (answer.transport) {
        try { pc.close(); } catch {}
        pcRef.current = null;
        dcRef.current = null;
        return answer;
      }
      sessionIdRef.current = answer.session_id;
      await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
      // The SDP answer alone does not confirm the Live session is running.
      await started;
      return null;
    } finally {
      clearTimeout(timer);
    }
  }, [api, handleEvent]);

  // Gemini / WS-proxy path. Consult auth is the HttpOnly cookie carried by the
  // same-origin upgrade; teach mode's one-time ws_ticket is already in `token`.
  const startGeminiCall = useCallback(async (stream, token) => {
    const wsUrl = buildGeminiWsUrl(token);
    await geminiRef.current.start(stream, wsUrl);
  }, []);

  const startCall = useCallback(async () => {
    if (state !== STATES.IDLE && state !== STATES.ERROR) return;
    const generation = ++attemptGenerationRef.current;
    const isCurrent = () => attemptGenerationRef.current === generation;
    setError(null); setCaptions([]); setElapsed(0); setFellBack(false); setTransport(null);
    setMuted(false);
    setModel(null); modelRef.current = null; liveUsageRef.current = null;
    setUsage({ cost: 0, tokens: {} });
    userEndRef.current = false;
    setState(STATES.REQUESTING_MIC);

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      if (!isCurrent()) return;
      setError('اجازهٔ میکروفون لازم است. لطفاً در تنظیمات مرورگر اجازه دهید یا با چت متنی ادامه دهید.');
      setState(STATES.ERROR); return;
    }
    if (!isCurrent()) { stream.getTracks().forEach((t) => t.stop()); return; }
    streamRef.current = stream;

    setState(STATES.CONNECTING);
    let token;
    try {
      // Consult passes location; teach passes nothing.
      token = mode === 'teach' ? await api.startCall() : await api.startCall(location);
    } catch (e) {
      if (!isCurrent()) return;
      teardownMedia();
      setError(e.message || 'برقراری تماس صوتی ناموفق بود.');
      setState(STATES.ERROR); return;
    }
    if (!isCurrent()) return;
    const applyToken = (t) => {
      sessionIdRef.current = t.session_id;
      setTransport(t.transport || 'webrtc');
      setFellBack(!!t.fell_back);
      modelRef.current = t.model || null;
      setModel(modelRef.current);
    };
    applyToken(token);

    try {
      let next = token;
      if (next.transport === 'webrtc-live') {
        liveUsageRef.current = { seconds: 0, input: 0, cached: 0, output: 0, responses: new Set() };
        next = await startLiveCall(stream, next);
        if (!isCurrent()) return;
        // OpenAI rejected the Live session: continue on the backup provider.
        if (next) { liveUsageRef.current = null; applyToken(next); }
      }
      if (next && next.transport === 'ws-proxy') {
        await startGeminiCall(stream, next);
      } else if (next) {
        await startWebRtcCall(stream, next);
      }
    } catch (e) {
      if (!isCurrent()) return;
      teardownMedia();
      try { await api.endCall(); } catch {}
      if (!isCurrent()) return;
      setError(e.message || 'اتصال صوتی برقرار نشد. لطفاً اینترنت‌تان را بررسی کنید یا با چت متنی ادامه دهید.');
      setState(STATES.ERROR); return;
    }

    if (!isCurrent()) return;
    startTsRef.current = Date.now();
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTsRef.current) / 1000));
    }, 1000);
    lastSentLocationRef.current = location;
    setState(STATES.ACTIVE);
  }, [state, mode, api, location, startWebRtcCall, startLiveCall, startGeminiCall, teardownMedia]);

  const endCall = useCallback(async () => {
    if (state === STATES.IDLE) return;
    attemptGenerationRef.current += 1;
    userEndRef.current = true;
    setState(STATES.ENDING);
    teardownMedia();
    try { await api.endCall(); } catch { /* best effort */ }
    setState(STATES.IDLE);
  }, [state, api, teardownMedia]);

  const toggleMute = useCallback(() => {
    const next = !isMuted;
    if (transport === 'ws-proxy') {
      geminiRef.current.toggleMute();
    } else if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
    }
    setMuted(next);
  }, [isMuted, transport]);

  useEffect(() => () => { attemptGenerationRef.current += 1; userEndRef.current = true; teardownMedia(); }, [teardownMedia]);

  // shareLocation: consult-mode only. Teach has no map / pin.
  const shareLocation = useCallback((loc) => {
    if (mode === 'teach') return false;
    if (state !== STATES.ACTIVE) return false;
    if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return false;
    if (transport === 'webrtc') {
      const dc = dcRef.current;
      if (!dc || dc.readyState !== 'open') return false;
      try {
        dc.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{
              type: 'input_text',
              text: `[به‌روزرسانی موقعیت - مشتری دکمهٔ «اشتراک موقعیت» را زده] موقعیتِ تأییدشدهٔ مشتری روی نقشه: عرض ${loc.lat}، طول ${loc.lng}. حتماً ابزار check_coverage را با همین مختصاتِ دقیق صدا بزن و بر اساس نتیجهٔ آن جواب بده.`,
            }],
          },
        }));
        dc.send(JSON.stringify({ type: 'response.create' }));
        lastSentLocationRef.current = loc;
        return true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[realtime] shareLocation (webrtc) failed', e && e.message);
        return false;
      }
    }
    if (transport === 'webrtc-live') {
      // The server queues the coordinates for the backend's check_coverage.
      api.shareLiveLocation(loc).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('[realtime] shareLocation (live) failed', e && e.message);
      });
      lastSentLocationRef.current = loc;
      return true;
    }
    if (transport === 'ws-proxy') {
      const ok = geminiRef.current.sendControlFrame({
        type: 'location_update', lat: loc.lat, lng: loc.lng,
      });
      if (ok) lastSentLocationRef.current = loc;
      return ok;
    }
    return false;
  }, [mode, state, transport, api]);

  return {
    state, captions, elapsedSec, error, isMuted,
    transport, fellBack, model,
    usage,
    startCall, endCall, toggleMute, shareLocation,
    _test_handleEvent: handleEvent,
  };
}
